'use client'

import { ColorCode, Icon } from '@vinted/web-ui'
import { ArrowRight16 } from '@vinted/monochrome-icons'
import classNames from 'classnames'

import { useBreakpoint } from '@marketplace-web/shared/breakpoints'
import {
  HoVCreatorsSectionCardModel,
  HoVCreatorsSectionModel,
} from '@marketplace-web/domain/marketing-landing-page-data'

import HoVCreatorsCard from './HoVCreatorsCard'
import useSlider from '../../../../hooks/useSlider'

type Props = { data: HoVCreatorsSectionModel }

export const HoVCreatorsSection = ({ data: { title, cards } }: Props) => {
  const {
    itemRefs,
    handleOnTouchStart,
    handleOnTouchMove,
    handleOnTouchEnd,
    goToNextItem,
    goToPreviousItem,
    wrapperDivRef,
    showButtons,
    currentCardIndex,
  } = useSlider({ childData: cards })

  const breakpoints = useBreakpoint()

  const renderButtonsGroup = (cardIndex: number) => {
    const previousButtonIsDisabled = cardIndex === 0
    const nextButtonIsDisabled = cardIndex === cards.length - 1

    return (
      <div className="hov__creators-slider-buttons-container">
        <button
          type="button"
          className={classNames(
            'hov__creators-slider-wrapper-button hov__creators-slider-rotate-180',
            {
              'hov__creators-slider-nav-disabled': previousButtonIsDisabled,
            },
          )}
          onClick={goToPreviousItem}
          data-testid="slider-previous"
          disabled={previousButtonIsDisabled}
        >
          <Icon
            name={ArrowRight16}
            color={previousButtonIsDisabled ? ColorCode.GreyscaleLevel5 : ColorCode.GreyscaleLevel1}
          />
        </button>
        <button
          type="button"
          className={classNames('hov__creators-slider-wrapper-button', {
            disabled: nextButtonIsDisabled,
          })}
          onClick={goToNextItem}
          data-testid="slider-next"
          disabled={nextButtonIsDisabled}
        >
          <Icon
            name={ArrowRight16}
            color={nextButtonIsDisabled ? ColorCode.GreyscaleLevel4 : ColorCode.GreyscaleLevel1}
          />
        </button>
      </div>
    )
  }

  const renderSlider = (data: Array<HoVCreatorsSectionCardModel>) => {
    return (
      <div
        className="hov__creators-section-slider-container"
        ref={wrapperDivRef}
        onTouchMove={handleOnTouchMove}
        onTouchStart={handleOnTouchStart}
        onTouchEnd={showButtons && breakpoints.wide ? undefined : handleOnTouchEnd}
      >
        {data.map((props, index) => (
          <div ref={itemRefs[index]} className="hov__creators-slider-wrapper-item" key={index}>
            <HoVCreatorsCard key={index} {...props} index={index} />
          </div>
        ))}
      </div>
    )
  }

  return (
    <section data-testid="hov-creators" className="hov__creators-section">
      <div className="hov__creators-section-inner">
        <div className="hov__creators-content">
          <h2 className="hov__creators-title">{title}</h2>
          {showButtons && renderButtonsGroup(currentCardIndex)}
        </div>
        <div className="hov__creators-cards-wrapper">{renderSlider(cards)}</div>
      </div>
    </section>
  )
}
